<template>
  <div
    v-if="visible"
    data-testid="qv-tightness-badge"
    class="flex justify-center text-sm text-slate-500"
  >
    <component
      :is="scrollTarget ? 'button' : 'span'"
      v-tooltip.bottom="tooltipText"
      :type="scrollTarget ? 'button' : undefined"
      :aria-label="scrollTarget ? ariaLabel : undefined"
      class="inline-flex items-center gap-2 rounded-md px-1"
      :class="scrollTarget
        ? 'cursor-pointer transition-colors hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400'
        : 'cursor-help'"
      @click="jumpToTarget"
    >
      <span v-if="hasPct">
        {{ $t('messages.quickValuation.live.tightness.title') }}
        <span class="font-medium text-slate-700 tabular-nums">{{ rangeLabel }}</span>
      </span>
      <Tag
        v-if="hasBin"
        data-testid="qv-tightness-chip"
        :value="binLabel"
        :severity="severity"
        class="text-xs!"
      />
      <span v-if="scrollTarget" class="pi pi-angle-down text-xs" aria-hidden="true"></span>
    </component>
  </div>
</template>

<script setup>
/**
 * RED-356 — LIVE copy (Toomas 02.10 15:40): while `qv-statistical-report` is OFF
 * the Kiirhindamine result page must show exactly what LIVE shows today, so
 * this is origin/main 969c0754's UncertaintyBadge.vue unchanged, except that the
 * strings dev rewrote are read from `messages.quickValuation.live.*`.
 * UncertaintyBadge.vue is the flag-ON version. Delete this file with the flag.
 *
 * RED-290 — per-valuation uncertainty badge (Jaan's method D).
 *
 * Renders „Selle hinnangu täpsus ±X %" plus a kitsas/keskmine/lai chip from
 * the persisted `prediction.tightness` object. `bin` and `x_pct` are shown
 * exactly as the ML service sent them — the chip is never re-derived from `u`
 * (the wire `u` is rounded, the server binned on the unrounded value).
 *
 * Three cases: number + chip (comps), chip only (direct low-confidence),
 * nothing (null payload / legacy rows — no space is reserved).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { scrollToSectionId } from '@/composables/useScrollShortcuts'

const props = defineProps({
  tightness: { type: Object, default: null },
  /** DOM id to jump to on click (e.g. `model-quality`); null renders a plain span. */
  scrollTarget: { type: String, default: null },
})

const { t, te } = useI18n()

const BIN_SEVERITY = {
  kitsas: 'success',
  keskmine: 'warn',
  lai: 'danger',
}

const hasPct = computed(() => Number.isFinite(props.tightness?.x_pct))
const binKey = computed(() => props.tightness?.bin ?? null)
const hasBin = computed(() => binKey.value !== null && te(`messages.quickValuation.live.tightness.bins.${binKey.value}`))
const visible = computed(() => hasPct.value || hasBin.value)

const rangeLabel = computed(() =>
  hasPct.value ? t('messages.quickValuation.tightness.range', { pct: props.tightness.x_pct }) : '',
)
const binLabel = computed(() =>
  hasBin.value ? t(`messages.quickValuation.live.tightness.bins.${binKey.value}`) : '',
)
const severity = computed(() => BIN_SEVERITY[binKey.value] ?? 'secondary')

const tooltipText = computed(() =>
  props.tightness?.path === 'direct'
    ? t('messages.quickValuation.live.tightness.tooltipDirect')
    : t('messages.quickValuation.live.tightness.tooltip'),
)

const ariaLabel = computed(() =>
  hasPct.value
    ? t('messages.quickValuation.live.tightness.aria', { pct: props.tightness.x_pct, bin: binLabel.value })
    : t('messages.quickValuation.live.tightness.ariaChipOnly', { bin: binLabel.value }),
)

const jumpToTarget = () => {
  if (props.scrollTarget) {
    scrollToSectionId(props.scrollTarget, { offset: 90 })
  }
}
</script>
